import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IDCard from '../components/IDCard';
import { composeCard } from '../lib/composeShare';
import { rasterizeLockup } from '../lib/photo';
import { useFlow } from '../state/flow';
import './Welcome.scss';

/** Right-pane content for /welcome — the composited card preview + disclaimer.
 * Lives inside WelcomeShareFlow's stable layout, layered with ShareRight so
 * only the right-side content fades between routes. */
export function WelcomeRight() {
  const { t } = useTranslation();
  const { cardImageDataUrl, photoDataUrl, setCardImage } = useFlow();
  const fallbackRef = useRef<HTMLDivElement | null>(null);
  const [preview, setPreview] = useState<string | null>(cardImageDataUrl);

  // Fallback: if /loading didn't manage to bake the card composite, bake it
  // here using the offscreen IDCard.
  useEffect(() => {
    if (preview) return;
    let alive = true;
    void (async () => {
      if (!fallbackRef.current) return;
      try {
        const lockup = await rasterizeLockup(fallbackRef.current);
        if (!alive) return;
        const card = await composeCard(lockup, photoDataUrl);
        if (!alive) return;
        setPreview(card);
        setCardImage(card);
      } catch {
        // Leave preview null; placeholder shows.
      }
    })();
    return () => {
      alive = false;
    };
  }, [preview, setCardImage, photoDataUrl]);

  return (
    <>
      <div className="welcome-right">
        {preview ? (
          <img className="welcome-right__card" src={preview} alt="" />
        ) : (
          <div className="welcome-right__card welcome-right__card--placeholder" />
        )}
        <p className="welcome-right__disclaimer">{t('welcome.disclaimer')}</p>
      </div>
      <div className="welcome-offscreen" aria-hidden="true">
        <IDCard ref={fallbackRef} />
      </div>
    </>
  );
}
